import { GET_ORDER_BY_ID } from '@graphql/events/GetOrderById'
import { useEffect, useState } from 'react'

export type PolledPaymentState =
  | 'idle'
  | 'processing'
  | 'confirmed'
  | 'failed'
  | 'timed_out'

export const usePaymentStatusPolling = (
  orderId?: string,
  enabled: boolean = false
) => {
  const [order, setOrder] = useState<any>()
  const [state, setState] = useState<PolledPaymentState>('idle')

  useEffect(() => {
    if (!enabled || !orderId) {
      setState('idle')
      return
    }

    let stopped = false
    let timer: ReturnType<typeof setTimeout> | undefined
    const deadline = Date.now() + 5 * 60 * 1000
    setState('processing')

    const poll = async () => {
      try {
        const { apolloClient } = await import('@libs/apollo-client')
        const response = await apolloClient.query({
          query: GET_ORDER_BY_ID,
          variables: { orderId },
          fetchPolicy: 'network-only',
        })
        if (stopped) return
        const nextOrder = response.data?.getOrderById
        setOrder(nextOrder)
        const status = nextOrder?.paymentStatus
        if (nextOrder?.paid || status === 'SUCCEEDED') {
          setState('confirmed')
          return
        }
        if (['FAILED', 'REFUNDED', 'CHARGED_BACK'].includes(status ?? '')) {
          setState('failed')
          return
        }
      } catch (_) {
        // Retry transient query failures until the five-minute deadline.
      }

      if (Date.now() >= deadline) {
        setState('timed_out')
        return
      }
      timer = setTimeout(poll, 3000)
    }

    poll()
    return () => {
      stopped = true
      if (timer) clearTimeout(timer)
    }
  }, [enabled, orderId])

  return { order, state }
}
